import { CommonModule } from '@angular/common';
import { Component, ContentChild, HostListener, Input, TemplateRef, type OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputIconModule } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { CognitoService } from '../../services/cognito.service';

@Component({
  selector: 'app-besharp-topbar',
  imports: [CommonModule, RouterModule, ButtonModule, InputTextModule, FloatLabelModule, InputIconModule, IconFieldModule, MenuModule],
  templateUrl: './besharp-topbar.component.html',
  styleUrl: './besharp-topbar.component.scss',
})
export class BesharpTopbarComponent implements OnInit {
  /**
   * The title displayed in the topbar.
   * @type {string | undefined}
   */
  @Input()
  title?: string;

  /**
   * The logo image URL or path to display in the topbar.
   * @type {string | undefined}
   */
  @Input()
  logo?: string;

  /**
   * Whether the topbar should stick to the top on scroll. Default: true.
   * @type {boolean}
   */
  @Input()
  stickyBar: boolean = true;

  /**
   * The scroll Y threshold (in px) after which the sticky effect is applied. Default: 50.
   * @type {number}
   */
  @Input()
  scrollThreshold: number = 50;

  /**
   * The height of the topbar in pixels. Default: 60.
   * @type {number}
   */
  @Input()
  height: number = 60;

  /**
   * The vertical gap (in px) around the logo image. Default: 4.
   * @type {number}
   */
  @Input()
  imageVerticalGap: number = 4;

  /**
   * The route to navigate to when the logo or home is clicked. Optional.
   * @type {string | undefined}
   */
  @Input()
  homeRoute?: string;

  /**
   * The configuration object for topbar action items and their size.
   * @type {BesharpTopbarItems | undefined}
   */
  @Input()
  topbarItems?: BesharpTopbarItems;

  /**
   * If you want the topbar to contain an hamburger menu for mobile resolutions
   * @type {boolean}
   */
  @Input()
  mobileMenu?: boolean;

  /**
   * The list of links we want to see in the mobile menu
   * @type { mobileMenuListItem[] }
   */
  @Input()
  mobileMenuList?: mobileMenuListItem[];

  /**
   * TemplateRef for custom content before the left section of the topbar.
   */
  @ContentChild('topbarLeftBefore') leftTemplateBefore?: TemplateRef<any>;
  /**
   * TemplateRef for custom content after the left section of the topbar.
   */
  @ContentChild('topbarLeftAfter') leftTemplateAfter?: TemplateRef<any>;
  /**
   * TemplateRef for custom content before the right section of the topbar.
   */
  @ContentChild('topbarRightBefore') rightTemplateBefore?: TemplateRef<any>;
  /**
   * TemplateRef for custom content after the right section of the topbar.
   */
  @ContentChild('topbarRightAfter') rightTemplateAfter?: TemplateRef<any>;

  /**
   * Indicates if the window has been scrolled past the scrollThreshold.
   * @type {boolean}
   */
  protected isScrolled: boolean = false;

  /**
   * Indicates if the mobile menù is open or not
   * @type {boolean}
   */
  protected isMobileMenuOpen: boolean = false;

  constructor(
    private cognitoService: CognitoService,
    private router: Router
  ) {}

  /**
   * Angular lifecycle hook. Currently does nothing on init.
   */
  ngOnInit(): void { 
    if(this.mobileMenu && !this.mobileMenuList) {
      throw "mobile menu list parameter is requested if mobile menu boolean property is true";
    }
  }

  /**
   * Listens to window scroll events and updates isScrolled based on scrollThreshold.
   */
  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.isScrolled = window.scrollY > this.scrollThreshold;
  }

  /**
   * Returns the CSS class string for a topbar item based on its properties and the itemsSize.
   * @param besharpItem - The topbar item to style.
   * @param beSharpItems - The configuration object containing itemsSize.
   * @returns {string | undefined} The computed class string or undefined.
   */
  getStyleClass(besharpItem: BesharpTopbarItem, beSharpItems: BesharpTopbarItems): string | undefined {
    if(beSharpItems.itemsSize) {
      let style = `squared-btn s-btn-${beSharpItems.itemsSize}`;
      if (!besharpItem.label) {
        style += ' no-label';
      }
      if(besharpItem.spinningBorder) {
        style += ' spinning-border';
      }
      return style;
    } else {
      return undefined;
    }
  }

  /**
   * Handle mobile menu item click - execute command and close menu
   */
  onMobileMenuItemClick(item: mobileMenuListItem): void {
    if (item.command) {
      item.command();
    }
    this.isMobileMenuOpen = false;
  }

  /**
   * We toggle the visibility of the mobile menu
   */
  toogleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  /**
   * Close mobile menu when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const hamburgerButton = target.closest('.hamburger-menu p-button');
    if (!hamburgerButton && this.isMobileMenuOpen) {
      this.isMobileMenuOpen = false;
    }
  }

  /**
   * Handle logout action
   */
  async onLogout(): Promise<void> {
    try {
      await this.cognitoService.signOut();
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      this.router.navigate(['/signin']);
    } catch (error) {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      this.router.navigate(['/signin']);
    }
  }
}

/**
 * Represents a single item in the topbar, such as a button or input.
 */
export type BesharpTopbarItem = {
  /**
   * The type of the item: 'input' or 'button'. Optional.
   */
  itemType?: 'input' | 'button' | undefined;
  /**
   * The label for the item. Optional.
   */
  label?: string;
  /**
   * The icon name or path for the item.
   */
  icon: string;
  /**
   * The position of the icon relative to the label.
   */
  iconPosition: 'left' | 'right' | 'top' | 'bottom';
  /**
   * Optional badge text to display on the item.
   */
  badge?: string;
  /**
   * The severity style for the badge. Optional.
   */
  badgeSeverity?: 'secondary' | 'danger' | 'contrast' | 'info' | 'warn' | 'help' | 'success' | undefined;
  /**
   * The severity style for the item. Optional.
   */
  severity?: 'secondary' | 'danger' | 'contrast' | 'info' | 'warn' | 'help' | 'success' | undefined;
  /**
   * Whether the button is outlined. Required.
   */
  outlined: boolean;
  /**
   * Whether to show a spinning border around the item. Optional.
   */
  spinningBorder?: boolean;
  /**
   * The command to execute when the item is clicked.
   */
  command: () => void;
  /** 
   * PrimeNG MenuItems for popup dropdown. Pass a stable reference, not an inline array. 
   */
  menuItems?: MenuItem[];
}

/**
 * Represents the configuration for a group of topbar items.
 */
export type BesharpTopbarItems = {
  /**
   * The size of the items: 'xs', 'sm', 'md', 'lg'. Optional.
   */
  itemsSize: 'xs' | 'sm' | 'md' | 'lg' | undefined;
  /**
   * The array of topbar items.
   */
  items: BesharpTopbarItem[];
}

export type mobileMenuListItem = {
  icon?: string;
  label: string;
  command: () => {};
};